import React, { useId, useState } from 'react';
import type { ConfiguredService } from '../../types/service.types';
import { ServiceItemsList } from './ServiceItemsList';
import { Button } from '@shared/components';
import { formatCurrency } from '@/app/utils/formatCurrency';
import { formatDateOnlyShort } from '@/app/utils/formatDate';
import type { PricedLineFigures } from '@features/quote/types/basketPricing.types';
import { inclusionsToggleHint } from '../../utils/inclusionsToggleHint';

interface ServiceItemProps {
    service: ConfiguredService;
    onRemove: (serviceId: string) => void;
    canRemove?: boolean;
    currency?: string;
    // Absent when the caller offers no date-edit affordance (Epic 2.4.6 slice 5).
    onEditDate?: () => void;
    // Replaces the default ServiceItemsList body (e.g. SMRiQ decline toggles);
    // inclusionsCount then drives the disclosure toggle and its count text.
    inclusionsSlot?: React.ReactNode;
    inclusionsCount?: number;
    /** Replaces the default "N items included" text when the count is not all inclusions. */
    inclusionsLabel?: string;
    inclusionsDefaultOpen?: boolean;
    pricedLine?: PricedLineFigures;
}

export const ServiceItem: React.FC<ServiceItemProps> = ({
    service,
    onRemove,
    canRemove = true,
    currency = 'GBP',
    onEditDate,
    inclusionsSlot,
    inclusionsCount,
    inclusionsLabel,
    inclusionsDefaultOpen = false,
    pricedLine,
}) => {
    const [showItems, setShowItems] = useState(inclusionsDefaultOpen);
    const inclusionsPanelId = useId();

    const itemCount = inclusionsCount ?? service.items?.length ?? 0;
    const hasItems = itemCount > 0;
    // A total-priced service carries the money itself; its items are descriptive and
    // are stored at GBP 0, so pricing them would show a zero ladder under a real price.
    const showItemPricing = service.costMode !== 'total';
    const countLabel = inclusionsLabel ?? `${itemCount} item${itemCount !== 1 ? 's' : ''} included`;

    return (
        <div className="border border-mp-border rounded-lg p-4 bg-mp-surface shadow-sm">
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <h4 className="font-medium text-mp-foreground mb-2">{service.name}</h4>

                    {service.description && (
                        <p className="text-sm text-mp-muted mb-2">{service.description}</p>
                    )}

                    <div className="text-sm text-mp-muted space-y-1">
                        <div className="flex items-center gap-2">
                            <span>Due Date: {formatDateOnlyShort(service.startDate)}</span>
                            {onEditDate && (
                                <Button
                                    type="button"
                                    onClick={onEditDate}
                                    className="text-mp-link underline px-0"
                                    size="xs"
                                    variant="clear"
                                    ariaLabel={`Change Date for ${service.name}`}
                                >
                                    Change Date
                                </Button>
                            )}
                        </div>
                        {service.category && <div>Category: {service.category}</div>}
                        {hasItems && (
                            <Button
                                type="button"
                                onClick={() => setShowItems((open) => !open)}
                                className="text-sm text-mp-link font-medium hover:underline flex-wrap px-0"
                                size="none"
                                variant="clear"
                                ariaExpanded={showItems}
                                ariaControls={inclusionsPanelId}
                            >
                                <span>{countLabel}</span>{' '}
                                <span>{inclusionsToggleHint(showItems)}</span>{' '}
                                <span className="sr-only">for {service.name}</span>
                            </Button>
                        )}
                    </div>

                    {hasItems && (
                        // Stays mounted while collapsed so the toggle's aria-controls target always exists.
                        <div
                            id={inclusionsPanelId}
                            hidden={!showItems}
                            className="mt-3 pt-3 border-t border-mp-border"
                        >
                            {inclusionsSlot ?? (
                                <ServiceItemsList
                                    items={service.items!}
                                    showPricing={showItemPricing}
                                    className="space-y-2"
                                    currency={currency}
                                    pricedLine={pricedLine}
                                />
                            )}
                        </div>
                    )}
                </div>

                <div className="ml-4 text-right">
                    <div className="text-lg font-semibold text-mp-foreground">
                        {formatCurrency(service.gross_price, currency)}
                    </div>
                    <div className="text-sm text-mp-muted">
                        Net: {formatCurrency(service.price, currency)}
                    </div>
                    <div className="text-xs text-mp-muted">
                        VAT: {formatCurrency(service.gross_price - service.price, currency)}
                    </div>

                    {canRemove && (
                        <Button
                            type="button"
                            onClick={() => onRemove(service.instanceId)}
                            className="mt-2 text-mp-error text-sm underline"
                            size="none"
                            variant="clear"
                        >
                            Remove
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
